import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableTableRow } from '@/components/monitors/ExpandableTableRow';
import useProgramData from '@/hooks/useProgramData/forMonitoringPage';
import { Program } from '@/lib/api/program';
import { nowJST } from '@/lib/date';

export function Monitoring() {
  // Current time state
  const [currentTime, setCurrentTime] = useState(nowJST());

  // Update current time every 2 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(nowJST());
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  const { t } = useTranslation(['programs', 'common']);

  const {
    programs,
    loading,
    error,
  } = useProgramData(undefined, undefined);

  // Date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('common:menu.monitoring')}
        </Typography>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': { py: 0.5 }, // Apply to all TableCells
            }}
            aria-label={t('programs:table.programList')}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('programs:table.id')}</TableCell>
                <TableCell>{t('programs:table.startTime')}</TableCell>
                <TableCell>{t('programs:table.endTime')}</TableCell>
                <TableCell>{t('programs:table.duration')}</TableCell>
                <TableCell>{t('programs:table.supplier')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                      {t('common:messages.loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : programs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('programs:messages.noPrograms')}
                  </TableCell>
                </TableRow>
              ) : (
                programs.map((program: Program) => (
                  <ExpandableTableRow
                    key={program.id}
                    program={program}
                    formatDate={formatDate}
                    currentTime={currentTime} // Pass current time
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

export default Monitoring;
